import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay,
  addMonths,
  subMonths,
  getDay,
  isToday,
  isBefore
} from "date-fns";
import { fr } from "date-fns/locale";
import InterventionsMap from "@/components/InterventionsMap";
import { 
  ChevronLeft, 
  ChevronRight, 
  X,
  Plus,
  Minus,
  Calendar,
  Clock,
  MapPin,
  Loader2,
  User,
  CheckCircle
} from "lucide-react";

const DAYS_OF_WEEK = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const formatDuration = (minutes) => {
  const m = parseInt(minutes, 10);
  if (!m || isNaN(m)) return null;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h > 0 && min > 0) return `${h} h ${min}`;
  if (h > 0) return `${h} h`;
  return `${min} min`;
};
const DAY_MAPPING = {
  1: "lundi",
  2: "mardi", 
  3: "mercredi",
  4: "jeudi",
  5: "vendredi",
  6: "samedi",
  0: "dimanche"
};
const SLOTS = [
  { id: "matin", label: "Matin", hours: "7h-12h" },
  { id: "apres_midi", label: "Après-midi", hours: "12h-18h" },
  { id: "soir", label: "Soir", hours: "18h-21h" }
];

const NurseCalendar = ({ onClose }) => {
  const { token } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCalendarData();
  }, [currentMonth]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/nurses/calendar?month=${currentMonth.getMonth() + 1}&year=${currentMonth.getFullYear()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCalendarData(data);
      }
    } catch (error) {
      console.error("Error fetching calendar:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleException = async (date, slot, makeAvailable) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/nurses/availability-exception`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          date: format(date, "yyyy-MM-dd"),
          slot: slot,
          available: makeAvailable
        })
      });

      if (response.ok) {
        toast.success(makeAvailable ? "Créneau ajouté" : "Créneau retiré");
        fetchCalendarData();
      }
    } catch (error) {
      toast.error("Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const removeException = async (date, slot) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/nurses/availability-exception`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          date: format(date, "yyyy-MM-dd"),
          slot: slot
        })
      });

      if (response.ok) {
        toast.success("Exception supprimée");
        fetchCalendarData();
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setSaving(false);
    }
  };

  // Check if a slot is available for a given date based on profile availability
  const isSlotAvailableByProfile = (date, slotId) => {
    if (!calendarData?.availability) return false;
    
    const dayOfWeek = getDay(date);
    const dayName = DAY_MAPPING[dayOfWeek];
    const key = `${dayName}_${slotId}`;
    
    return calendarData.availability[key] === true;
  };

  // Check for exceptions on a specific date/slot
  const getException = (date, slotId) => {
    if (!calendarData?.exceptions) return null;
    
    const dateStr = format(date, "yyyy-MM-dd");
    return calendarData.exceptions.find(e => e.date === dateStr && e.slot === slotId);
  };

  // Final availability status considering both profile and exceptions
  const getSlotStatus = (date, slotId) => {
    const profileAvailable = isSlotAvailableByProfile(date, slotId);
    const exception = getException(date, slotId);
    
    if (exception) {
      return {
        available: exception.available,
        isException: true,
        exceptionType: exception.available ? "added" : "removed"
      };
    }
    
    return {
      available: profileAvailable,
      isException: false,
      exceptionType: null
    };
  };

  // Get interventions for a specific date
  const getInterventionsForDate = (date) => {
    if (!calendarData) return [];
    
    const dateStr = format(date, "yyyy-MM-dd");
    const interventions = [];
    
    // Care requests
    calendarData.care_requests?.forEach(cr => {
      if (cr.date === dateStr) {
        interventions.push({
          type: "ponctuelle",
          id: cr.request_id,
          care_types: cr.care_types || [cr.care_type],
          time: cr.time,
          duration_minutes: cr.duration_minutes,
          address: cr.address,
          city: cr.city,
          postal_code: cr.postal_code,
          patient_name: cr.patient_name,
          status: cr.status
        });
      }
    });
    
    // Chronological order by start time (07:00 → 20:00)
    interventions.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return interventions;
  };

  // Check if any slot has availability for a day
  const getDayStatus = (date) => {
    let hasAvailable = false;
    let hasIntervention = false;
    let hasException = false;
    
    SLOTS.forEach(slot => {
      const status = getSlotStatus(date, slot.id);
      if (status.available) hasAvailable = true;
      if (status.isException) hasException = true;
    });
    
    const interventions = getInterventionsForDate(date);
    if (interventions.length > 0) hasIntervention = true;
    const hasPendingPayment = interventions.some(i => i.status === "pending_payment");
    
    return { hasAvailable, hasIntervention, hasException, hasPendingPayment };
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Add padding days for the first week
  const startDayOfWeek = getDay(monthStart);
  const paddingDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0D1C32] rounded-xl border border-[#2A3A4D] w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-[#2A3A4D] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-[#00E073]" />
            <h2 className="text-xl font-semibold text-white">Mon calendrier</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#00E073] animate-spin" />
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Calendar View */}
              <div>
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <h3 className="text-lg font-semibold text-white capitalize">
                    {format(currentMonth, "MMMM yyyy", { locale: fr })}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>

                {/* Days Header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {DAYS_OF_WEEK.map(day => (
                    <div key={day} className="text-center text-xs text-[#64748B] py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Padding days */}
                  {Array.from({ length: paddingDays }).map((_, i) => (
                    <div key={`pad-${i}`} className="aspect-square" />
                  ))}
                  
                  {/* Actual days */}
                  {calendarDays.map(day => {
                    const { hasAvailable, hasIntervention, hasException, hasPendingPayment } = getDayStatus(day);
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isPast = isBefore(day, new Date()) && !isToday(day);
                    
                    return (
                      <button
                        key={day.toISOString()}
                        onClick={() => setSelectedDate(day)}
                        disabled={isPast}
                        className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-all relative ${
                          isSelected 
                            ? "bg-[#00E073] text-[#0D1C32] font-bold" 
                            : isPast
                              ? "text-[#64748B] cursor-not-allowed"
                              : isToday(day)
                                ? "bg-[#1F2A38] text-white ring-2 ring-[#00E073]"
                                : "bg-[#1F2A38] text-white hover:bg-[#2A3A4D]"
                        }`}
                      >
                        {format(day, "d")}
                        
                        {/* Status indicators */}
                        <div className="flex gap-0.5 mt-0.5">
                          {hasAvailable && !isSelected && (
                            <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                          )}
                          {hasIntervention && !hasPendingPayment && !isSelected && (
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                          )}
                          {hasPendingPayment && !isSelected && (
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                          )}
                          {hasException && !isSelected && (
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#94A3B8]">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-400" /> Disponible
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-400" /> Intervention
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-orange-400" /> En attente de paiement
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-400" /> Exception
                  </span>
                </div>
              </div>

              {/* Day Detail View */}
              <div className="bg-[#1F2A38] rounded-xl p-4">
                {selectedDate ? (
                  <>
                    <h4 className="text-lg font-semibold text-white mb-4 capitalize">
                      {format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}
                    </h4>

                    {/* Slots */}
                    <div className="space-y-3 mb-6">
                      <p className="text-sm text-[#94A3B8] font-medium">Disponibilités</p>
                      {SLOTS.map(slot => {
                        const status = getSlotStatus(selectedDate, slot.id);
                        
                        return (
                          <div 
                            key={slot.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              status.available 
                                ? status.isException
                                  ? "bg-amber-500/10 border-amber-500/30"
                                  : "bg-green-500/10 border-green-500/30"
                                : status.isException
                                  ? "bg-red-500/10 border-red-500/30"
                                  : "bg-[#0D1C32] border-[#2A3A4D]"
                            }`}
                          >
                            <div>
                              <p className={`font-medium ${status.available ? "text-green-400" : "text-[#64748B]"}`}>
                                {slot.label}
                              </p>
                              <p className="text-xs text-[#64748B]">{slot.hours}</p>
                              {status.isException && (
                                <p className="text-xs text-amber-400 mt-1">
                                  {status.exceptionType === "added" ? "Ajouté exceptionnellement" : "Retiré exceptionnellement"}
                                </p>
                              )}
                            </div>
                            
                            <div className="flex gap-2">
                              {status.isException ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-xs"
                                  onClick={() => removeException(selectedDate, slot.id)}
                                  disabled={saving}
                                >
                                  Annuler exception
                                </Button>
                              ) : status.available ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-400 border-red-400/30 text-xs"
                                  onClick={() => toggleException(selectedDate, slot.id, false)}
                                  disabled={saving}
                                >
                                  <Minus className="w-3 h-3 mr-1" />
                                  Indisponible
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-xs"
                                  onClick={() => toggleException(selectedDate, slot.id, true)}
                                  disabled={saving}
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Disponible
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Interventions */}
                    {(() => {
                      const interventions = getInterventionsForDate(selectedDate);
                      return interventions.length > 0 && (
                        <div>
                          <p className="text-sm text-[#94A3B8] font-medium mb-3">
                            Interventions programmées ({interventions.length})
                          </p>
                          <div className="space-y-3">
                            {interventions.map((intervention, idx) => (
                              <div 
                                key={intervention.id || idx}
                                className={`rounded-lg p-3 border ${
                                  intervention.status === "pending_payment"
                                    ? "bg-amber-500/10 border-amber-500/40 border-dashed"
                                    : (intervention.status === "completed_by_nurse" || intervention.status === "completed")
                                      ? "bg-green-500/5 border-green-500/30"
                                      : "bg-blue-500/10 border-blue-500/30"
                                }`}
                              >
                                {/* Badges row */}
                                <div className="flex flex-wrap items-center gap-1 mb-2">
                                  <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                                    Demande de soin
                                  </Badge>
                                  {intervention.status === "pending_payment" && (
                                    <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/40 text-xs flex items-center gap-1" data-testid={`calendar-pending-payment-${idx}`}>
                                      <Clock className="w-3 h-3" />
                                      En attente de paiement
                                    </Badge>
                                  )}
                                  {(intervention.status === "completed_by_nurse" || intervention.status === "completed") && (
                                    <Badge className="bg-green-500/20 text-green-400 border border-green-500/40 text-xs flex items-center gap-1" data-testid={`calendar-done-stamp-${idx}`}>
                                      <CheckCircle className="w-3 h-3" />
                                      Réalisé
                                    </Badge>
                                  )}
                                </div>
                                
                                {/* Patient name */}
                                {intervention.patient_name && (
                                  <div className="flex items-center gap-2 text-sm text-white mb-2">
                                    <User className="w-4 h-4 text-[#00E073]" />
                                    <span className="font-medium">{intervention.patient_name}</span>
                                  </div>
                                )}
                                
                                {/* Time and location details */}
                                <div className="space-y-1 text-xs text-[#94A3B8]">
                                  {intervention.time && (
                                    <div className="flex items-center gap-2">
                                      <Clock className="w-3 h-3" />
                                      <span>
                                        {intervention.time}
                                        {formatDuration(intervention.duration_minutes) && (
                                          <span className="text-[#00E073] ml-1">· {formatDuration(intervention.duration_minutes)}</span>
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex items-start gap-2">
                                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    <span>
                                      {intervention.address && `${intervention.address}, `}
                                      {intervention.city}
                                      {intervention.postal_code && ` (${intervention.postal_code})`}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Description for recurring interventions */}
                                {intervention.description && (
                                  <p className="text-xs text-[#64748B] mt-2 italic border-t border-[#2A3A4D] pt-2">
                                    "{intervention.description}"
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="mt-4">
                            <p className="text-sm text-[#94A3B8] font-medium mb-2 flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-[#00E073]" />
                              Carte de la tournée
                            </p>
                            <InterventionsMap interventions={interventions} />
                          </div>
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className="text-center py-12 text-[#64748B]">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Sélectionnez un jour pour voir les détails</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#2A3A4D] flex justify-end">
          <Button onClick={onClose} className="btn-primary">
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NurseCalendar;
